import React, { FC, useContext, useState } from 'react';
import { useQueryLoader } from 'react-relay';
import { WeeklyGoalIndicatorQuery } from '../graphQLOperations/__generated__/WeeklyGoalIndicatorQuery.graphql';
import { getWeeklyGoalIndicator } from '../graphQLOperations/WeeklyGoal';
import { useFlag } from '@unleash/proxy-client-react';
import { GoalValue } from '../components/WeeklyGoal/WeeklyGoalWrapper';

const WeeklyGoalContext = React.createContext<{
  queryRef: ReturnType<typeof useQueryLoader<WeeklyGoalIndicatorQuery>>[0];
  loadQuery: ReturnType<typeof useQueryLoader<WeeklyGoalIndicatorQuery>>[1];
  overrideData: GoalValue[] | null;
  addGoalOverride: (goal: GoalValue) => void;
  lastAddedGoal: GoalValue | null;
  clearLastAddedGoal: () => void;
  setLastAddedGoal: React.Dispatch<React.SetStateAction<GoalValue | null>>;
} | null>(null);

interface Props {
  children: React.ReactNode;
}

export const LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG = 'LTS-shutoff-PP-435-weekly-goal';

export const WeeklyGoalProvider: FC<Props> = ({ children }) => {
  const isEnabled = !useFlag(LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG);

  const [queryRef, loadQuery] = useQueryLoader<WeeklyGoalIndicatorQuery>(getWeeklyGoalIndicator);
  const [overrideData, setOverrideData] = useState<GoalValue[] | null>(null);
  const [lastAddedGoal, setLastAddedGoal] = useState<GoalValue | null>(null);

  if (!isEnabled) {
    return <>{children}</>;
  }

  const addGoalOverride = (goal: GoalValue) => {
    setOverrideData(prev => {
      const updated = prev ? [goal, ...prev] : [goal];
      return updated;
    });
  };

  const clearLastAddedGoal = () => {
    setLastAddedGoal(null);
  };

  return (
    <WeeklyGoalContext.Provider
      value={{
        queryRef,
        loadQuery,
        overrideData,
        addGoalOverride,
        lastAddedGoal,
        clearLastAddedGoal,
        setLastAddedGoal,
      }}
    >
      {children}
    </WeeklyGoalContext.Provider>
  );
};

export const useWeeklyGoal = () => {
  return useContext(WeeklyGoalContext);
};
